import getCategoriesSelectApi from '@/api/get/get-categories-select';
import createCategoryApi from '@/api/post/create-category';
import theme from '@/common/theme';
import Alert from '@/components/alert/alert-component';
import Loader from '@/components/loader/loader-component';
import CreateEditModal from '@/components/modal/modal-component';
import Checkbox from '@/components/react-hook-form-inputs/checkbox/checkbox-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { CategoryAddEditSubForm } from '@/pages/categories/category-add-edit-form';
import { recursiveFlatMap } from '@/pages/company-management/company-products/utils';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  MdAdd,
  MdChevronRight,
  MdKeyboardArrowDown,
  MdOutlineSearch,
} from 'react-icons/md';
import PropTypes from 'prop-types';
import Styled from './product-categories-style';

const noPaddingLeft = {
  paddingLeft: 0,
};

/**
 * Product Categories component for the product form
 * Maintains a string array of category ID's that are linked or unlinked form the product
 * Product Edit component determines what is added (by `category_id`) or removed (by `product_category_link_id`)
 * @param {Function} handleMultiSelectChange - function to handle the multi select change
 * @returns {JSX.Element}
 */
const ProductCategories = ({ handleMultiSelectChange }) => {
  const [treeData, setTreeData] = useState([]);
  const containerRef = useRef(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [modalAction, setModalAction] = useState({ label: '', id: null });
  const { watch, setValue, control } = useFormContext();
  const getSearchTerm = watch('search_categories');
  const selectedItems = useWatch({
    control,
    name: 'categories',
    defaultValue: [],
  });
  const [searchTerm, setSearchTerm] = useState(getSearchTerm);

  const {
    data: allCategoriesData,
    loading: allCategoriesIsLoading,
    refetch,
  } = useLazyQuery(getCategoriesSelectApi);

  const [createCategory, { loading: createCategoryLoading }] =
    useMutation(createCategoryApi);

  // add or remove the node id from the selected Array
  const handleSelection = (node, event) => {
    const { checked } = event.target;

    // make a deep copy
    const temp = JSON.parse(JSON.stringify(selectedItems));

    // add to temp selection
    const add = (id) => {
      temp.push(id);
    };
    // remove from temp selection
    const remove = (id) => {
      // IF the id exists in the already selected items copy remove it
      const index = temp.indexOf(id);
      if (index > -1) {
        temp.splice(index, 1);
      }
    };

    const addOrRemove = (id) => {
      // IF the id exists in the already selected items copy remove it
      const index = temp.indexOf(id);
      if (index > -1 && !checked) {
        temp.splice(index, 1);
      } else if (index === -1 && checked) {
        // ELSE add it
        add(id);
      }
    };

    addOrRemove(node.id);

    // if it has children
    if (node?.children?.length > 0) {
      // function to recursively check/uncheck all children and children's children
      const checkUncheckNodeChildren = (children) => {
        children.forEach((k) => {
          // we must add or remove based on checked boolean
          if (checked) {
            // if exists it we mustn't add twice
            if (!temp?.includes(k.id)) {
              add(k.id);
            }
          } else {
            // else remove
            remove(k.id);
          }
          if (k?.children?.length > 0) {
            checkUncheckNodeChildren(k.children);
          }
        });
      };
      // call recursion on clicked node's children
      checkUncheckNodeChildren(node.children);
    }

    // if it has a parent
    if (node?.parent_category) {
      // function to recursively check if all the parents should be marked as selected
      const checkParentsRequireChecked = (parentId) => {
        const flatTree = recursiveFlatMap(treeData, [], true);
        const parentNode = flatTree?.find((item) => item.id === parentId);
        const { parent_category: parentCategory, children } = parentNode;
        // if it has children
        if (children?.length > 0) {
          // if all children are selected, add it
          if (children?.map((p) => p.id).some((p) => temp.includes(p))) {
            add(parentId);
          }
          // else remove it
          else {
            remove(parentId);
          }
        }
        if (parentCategory) {
          checkParentsRequireChecked(parentCategory);
        }
      };
      // call recursion on clicked node's parent
      checkParentsRequireChecked(node.parent_category);
    }

    setValue('categories', temp);
    handleMultiSelectChange?.('categories', temp);
  };

  const toggleChildNodes = (nodes, nodeId) =>
    nodes.map((node) => {
      if (node.id === nodeId) {
        return { ...node, isOpen: !node.isOpen };
      }
      if (node.children.length) {
        return { ...node, children: toggleChildNodes(node.children, nodeId) };
      }
      return node;
    });

  const toggleNode = (nodeId) => {
    setTreeData((prevTreeData) =>
      prevTreeData.map((node) => {
        if (node.id === nodeId) {
          return { ...node, isOpen: !node.isOpen };
        }
        if (node.children.length) {
          return { ...node, children: toggleChildNodes(node.children, nodeId) };
        }
        return node;
      }),
    );
  };

  const closeCreateEditModal = () => {
    setModalAction({ label: '', id: null });
  };

  const openCreateEditModal = () => {
    setModalAction({ label: 'New', id: null });
  };

  const filterNodes = (nodes) =>
    nodes.filter((node) => {
      const matchesSearchTerm = node.name
        .toLowerCase()
        .includes(searchTerm?.toLowerCase());
      const childMatches =
        node.children.length > 0 &&
        filterNodes(node.children, searchTerm).length > 0;
      return matchesSearchTerm || childMatches;
    });

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      setShowScrollbar(container.scrollHeight > container.clientHeight);
    }
  }, []);

  useEffect(() => {
    if (getSearchTerm !== searchTerm) setSearchTerm(getSearchTerm);
  }, [searchTerm, getSearchTerm]);

  useEffect(() => {
    const initializeTree = (data) => {
      if (!data) return [];
      const tree = {};
      data.forEach((item) => {
        tree[item.id] = { ...item, children: [], isOpen: false };
      });
      Object.values(tree).forEach((item) => {
        if (item.parent_category) {
          tree[item.parent_category].children.push(item);
        }
      });
      return Object.values(tree).filter(
        (item) => item.parent_category === null,
      );
    };

    setTreeData(initializeTree(allCategoriesData));
  }, [allCategoriesData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const recursiveCheck = (node, type) => {
    let retVal = false;
    if (!node) return retVal;
    if (selectedItems?.includes(node.id)) {
      retVal = true;
    }
    if (node?.children?.length > 0) {
      retVal = node?.children[type]((child) => recursiveCheck(child, type));
    }
    return retVal;
  };

  const renderNodes = (nodes) => (
    <Styled.ListWrapper>
      {nodes.map((node) => {
        // determines if the checkbox is checked
        const selected = recursiveCheck(node, 'every');

        // indeterminate checkbox status
        const partiallySelected =
          // NOT every node is selected
          !selected &&
          // AND some are selected
          recursiveCheck(node, 'some');

        return (
          <Styled.ListItem key={node.id} $hasParent={node.parent_category}>
            <Styled.ItemLabel>
              <Styled.CloseOpenButton
                $hasChildren={node.children.length > 0}
                className={`icon ${node.isOpen ? 'open' : 'closed'}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleNode(node.id);
                }}
              >
                {node.isOpen ? <MdKeyboardArrowDown /> : <MdChevronRight />}
              </Styled.CloseOpenButton>
              <Checkbox
                indeterminate={partiallySelected}
                label={node.name}
                name={node.id}
                checked={selected}
                style={noPaddingLeft}
                onClick={(e) => handleSelection(node, e)}
              />
            </Styled.ItemLabel>

            {node.isOpen &&
              node.children.length > 0 &&
              renderNodes(node.children)}
          </Styled.ListItem>
        );
      })}
    </Styled.ListWrapper>
  );

  const onSubmitForm = (formData) => {
    /**
     * We get is_multi_select as a string from the form.
     * We need to convert it to a boolean to send to the API is_multi_select true/false.
     */
    const newFormData = {
      ...formData,
      is_multi_select: formData.is_multi_select === 'is_multi_select',
    };

    // if update icon is null, set it to default
    if (newFormData.icon === null) {
      newFormData.icon = 'default';
    }
    createCategory(newFormData)
      .then(() => {
        refetch();
        closeCreateEditModal();
        Alert({
          icon: 'success',
          text: 'Category created successfully',
        });
      })
      .catch(() => {
        Alert({
          icon: 'error',
          text: 'There was a problem creating your category',
        });
      });
  };

  const filteredTreeData = filterNodes(treeData);

  if (allCategoriesIsLoading) return <Loader loading />;

  const searchInputStyle = {
    width: '100%',
    height: '40px',
    borderRadius: '4px',
  };
  return (
    <>
      <Input
        placeholder="Search..."
        name="search_categories"
        iconLeft={<MdOutlineSearch size="20px" />}
        onChange={(e) => setSearchTerm(e.target.value)}
        inputStyle={searchInputStyle}
        $borderColor={`1px solid ${theme.COLOR_GREY5}`}
      />
      <Styled.Container ref={containerRef} $showScrollbar={showScrollbar}>
        <Styled.CategoriesContainer>
          {filteredTreeData.length > 0 ? (
            renderNodes(filteredTreeData)
          ) : (
            <>No matching items found.</>
          )}
        </Styled.CategoriesContainer>
        <Styled.CategoryUploadAction onClick={openCreateEditModal}>
          <MdAdd /> <Styled.ActionText>Add New Category</Styled.ActionText>
        </Styled.CategoryUploadAction>

        <CreateEditModal
          open={!!modalAction.label}
          close={closeCreateEditModal}
          width="30%"
          alignItems="flex-start"
          title={`${modalAction.label} Category`}
          titleFontSize="28px"
          bodyStyle={{ width: '100%', padding: 0 }}
          loading={createCategoryLoading}
        >
          {/* <FormWrapper onSubmit={onSubmitForm}> */}
          <CategoryAddEditSubForm
            setModalAction={setModalAction}
            modalAction={modalAction}
            loading={createCategoryLoading}
            onSubmit={onSubmitForm}
          />
          {/* </FormWrapper> */}
        </CreateEditModal>
      </Styled.Container>
    </>
  );
};

ProductCategories.defaultProps = {
  handleMultiSelectChange: undefined,
};

ProductCategories.propTypes = {
  handleMultiSelectChange: PropTypes.func,
};

export default ProductCategories;
